import { ComponentType } from '@angular/cdk/overlay';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Route, Routes } from '@angular/router';
import { Observable, Subscription, from, throwError } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { DialogName } from '../dialogs.config';
import { ActionData, ActionLinkObserver } from './action-link-observer.service';

/** A dialog component may declare its own `static dialogConfig`, so it loads together with the dialog. */
interface DialogWithConfig {
  dialogConfig?: MatDialogConfig;
}

interface LoadedDialog<T> {
  component: ComponentType<any>;
  config: MatDialogConfig;
  data: T;
}

/** Lazy dialog loader. Use this class as a canActivate guard within a regular lazy loading route. */
@Injectable({
  providedIn: 'root',
})
export class LazyDialogLoader extends ActionLinkObserver implements OnDestroy {
  private dialogService = inject(MatDialog);

  private dialogs = new Map<string, Route>();

  private readonly sub: Subscription;

  constructor() {
    super();

    /** Builds the dialogs stream */
    this.sub = this.observers$
      .pipe(
        switchMap(({ route }) => {
          // Extracts the dialog input data from the query parameters
          const data = this.actionData(route);

          return this.loadDialog<ActionData>(route.routeConfig, data).pipe(
            switchMap(({ component, config, data }) =>
              this.openDialogComponent<ActionData, unknown>(
                component,
                config,
                data
              )
            )
          );
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  /** Activate a dialog programmatically */
  public open<T, R>(dialog: DialogName, data?: T): Observable<R> {
    // Seeks for the requested dialog within the routes
    const routeConfig = this.seekDialog(dialog);

    if (!routeConfig) {
      return throwError(
        () =>
          new Error(`
      Unable to find the requested dialog "${dialog}".
      Make sure the corresponding Route exists within the same module this DialogLoader instance is provided.
    `)
      );
    }

    return this.loadDialog<T>(routeConfig, data).pipe(
      switchMap(({ component, config, data }) =>
        this.openDialogComponent<T, R>(component, config, data)
      )
    );
  }

  public preloadDialog(dialog: DialogName) {
    // Seeks for the requested dialog within the routes
    const routeConfig = this.seekDialog(dialog);

    if (!routeConfig) {
      return throwError(
        () =>
          new Error(`
      Unable to find the requested dialog "${dialog}".
      Make sure the corresponding Route exists within the same module this DialogLoader instance is provided.
    `)
      );
    }

    return this.loadDialog(routeConfig).pipe(first()).subscribe();
  }

  private loadDialog<T>(
    routeConfig: Route,
    data?: T
  ): Observable<LoadedDialog<T>> {
    return from(
      routeConfig.loadComponent() as Promise<ComponentType<any>>
    ).pipe(
      map(component => {
        const config: MatDialogConfig = {
          ...(component as DialogWithConfig).dialogConfig,
          ...routeConfig.data?.['dialogConfig'],
        };

        return { component, config, data };
      })
    );
  }

  private openDialogComponent<T, R>(
    component: ComponentType<any>,
    config: MatDialogConfig,
    data: T
  ): Observable<R> {
    return this.dialogService
      .open<unknown, T, R>(component, { ...config, data })
      .afterClosed()
      .pipe(first());
  }

  /** Seeks for the requested dialog route configuration */
  private seekDialog(dialog: string): Route {
    // Checks within the cached values first
    const route = this.dialogs.get(dialog);
    if (route) {
      return route;
    }

    const parseTree: any = (dialog: string, match: Route, routes: Routes) => {
      // Parses the router configuration tree otherwise
      return routes.reduce((match, route) => {
        // Recurs down the children
        if ('children' in route) {
          return parseTree(dialog, match, route.children);
        }

        // Does something only when the LazyDailogLoader is there
        if (route.canActivate?.indexOf(LazyDialogLoader) >= 0) {
          // Stores the dialog in the cache for the future
          this.dialogs.set(route.path, route);

          // Returns the ,matching value
          if (route.path === dialog) {
            return route;
          }
        }

        return match;
      }, match);
    };

    return parseTree(dialog, undefined, this.router.config);
  }
}
